import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { SUPPORT_WHATSAPP, WHATS_NEW_ITEMS, WHATS_NEW_RULES, WhatsNewItem, WhatsNewState } from './whats-new.config';
import { pendingWhatsNew } from './whats-new.filter';
import { WhatsNewApiService } from './whats-new-api.service';
import { WHATS_NEW_TITLE_ID, WhatsNewModalComponent } from './whats-new-modal/whats-new-modal.component';
import { createStoryVideo, releaseStoryVideo, whenPlayable } from './whats-new-video';

/**
 * Abre as novidades pendentes depois do login, todas numa modal só (em stories). Tudo é silencioso: falha ao ler o
 * estado ou ao marcar como vista nunca vira alerta (o usuário não pediu nada), a modal só deixa
 * de aparecer ou volta no próximo carregamento.
 *
 * Em duas etapas: `prepare()` roda assim que o app abre (lê o estado e já começa a baixar os vídeos das
 * pendentes) e `showPending()` abre a modal depois, quando o tour não abriu. `release()` desiste dos
 * vídeos quando o tour abre.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly api = inject(WhatsNewApiService);
  private readonly modal = inject(NgbModal);
  private readonly permission = inject(PermissionService);
  private readonly items = inject(WHATS_NEW_ITEMS);

  // Uma passada por carregamento da página: o master-page monta de novo a cada navegação de layout.
  private prepared?: Promise<WhatsNewItem[]>;
  private started = false;
  private released = false;
  /** Vídeos baixando desde o `prepare()`, por id; a modal adota esses mesmos elementos. */
  private preloaded = new Map<string, HTMLVideoElement>();

  /** Lê as pendentes e começa a baixar os vídeos delas (até 2). Uma vez por carregamento. */
  prepare(): Promise<WhatsNewItem[]> {
    return (this.prepared ??= this.load());
  }

  async showPending(): Promise<void> {
    if (this.started) return;
    this.started = true;

    const pending = await this.prepare();
    if (pending.length === 0) return;

    // O primeiro vídeo já vem baixando: espera dar pra tocar (com teto), pra modal abrir e andar logo.
    await whenPlayable(this.preloaded.get(pending[0].id), WHATS_NEW_RULES.preloadWait * 1000);

    const ref = this.modal.open(WhatsNewModalComponent, {
      fullscreen: 'sm',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'whats-new-dialog',
      ariaLabelledBy: WHATS_NEW_TITLE_ID,
    });
    ref.componentInstance.items = pending;
    // A modal passa a ser dona dos vídeos (e para o download deles ao fechar).
    ref.componentInstance.preloaded = this.preloaded;
    this.preloaded = new Map();
    // Marca quando a novidade aparece, sem esperar: quem fecha a aba no meio do vídeo não vê a mesma
    // novidade de novo. As que não apareceram (clique no botão principal antes) voltam no próximo
    // carregamento.
    ref.componentInstance.onShow = (item: WhatsNewItem) => this.markSeen(item.id);
    try {
      await ref.result;
    } catch {
      // Dispensada sem resultado (ex.: navegação): nada a fazer.
    }
  }

  /** O tour abriu no lugar das novidades: para o download dos vídeos. */
  release(): void {
    this.released = true;
    this.preloaded.forEach((video) => releaseStoryVideo(video));
    this.preloaded.clear();
  }

  private async load(): Promise<WhatsNewItem[]> {
    let state: WhatsNewState;
    try {
      state = await firstValueFrom(this.api.getState());
      await this.permission.ensureLoaded();
    } catch {
      return [];
    }

    const pending = pendingWhatsNew(this.items, state, new Date(), {
      hasRole: (role) => this.permission.has(role),
      supportAvailable: SUPPORT_WHATSAPP !== '',
    });

    // Movimento reduzido mostra só o pôster: nada de vídeo.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.released && !reducedMotion) {
      for (const item of pending) this.preloaded.set(item.id, createStoryVideo(item.videoUrl));
    }
    return pending;
  }

  // Fire-and-forget: uma nova tentativa e a segunda falha é ignorada.
  private markSeen(id: string): void {
    firstValueFrom(this.api.markSeen(id))
      .catch(() => firstValueFrom(this.api.markSeen(id)))
      .catch((): void => undefined);
  }
}
